import * as React from 'react';
import {RouteComponentProps, withRouter} from 'react-router';
import {ColumnProps} from 'antd/lib/table';
import {Button, Switch, Table} from 'antd';
import {Link} from 'react-router-dom';

import locales from '../locales';
import {ErrorAlert} from '../common/ErrorAlert';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import furoApi from '../api/FuroApi';
import {ErrorResponse, GridRequest, InventorySale, PageResponseEnvelope,} from '../api/dtos/dtos';
import * as tableUtils from '../utils/table-utils';
import {GridState, searchableSortableColumn, simpleColumn, sortableColumn} from '../utils/table-utils';
import ButtonBar from '../common/ButtonBar';
import * as dateUtils from '../utils/date-utils';
import {groupNumber, renderFixedPriceColumn} from '../utils/number-utils';
import * as paths from '../paths';
import {viewSalePath} from '../paths';
import InvoiceCell from "../invoicing/InvoiceCell";

interface SalesPageState extends GridState<InventorySale> {
  error?: ErrorResponse;
  showDeletedOnly: boolean;
}

class SalesPage extends React.Component<RouteComponentProps, SalesPageState> {

  public state: SalesPageState = {
    ...tableUtils.initialGridState(),
    sortColumnKey: 'createdAt',
    sortOrder: 'descend',
    showDeletedOnly: false,
    ...this.props.location.state,
  };

  public readonly loadingHandler?: LoadingOverlayHandler;

  public componentDidMount() {
    this.reload();
  }

  public render() {
    const {error, showDeletedOnly} = this.state;

    const columns: Array<ColumnProps<InventorySale>> = [
      searchableSortableColumn(locales.id, 'saleNumber', this.state, r => r.saleNumber),
      sortableColumn(locales.created_at, 'createdAt', this.state, this.renderCreatedAt),
      sortableColumn(locales.last_changed_at, 'lastChangedAt', this.state, this.renderLastChangedAt),
      searchableSortableColumn(locales.last_changed_by, 'lastChangedBy', this.state, this.renderLastChangedBy),
      searchableSortableColumn(locales.client, 'clientName', this.state, this.renderClient),
      simpleColumn(locales.entries, this.renderEntries),
      sortableColumn(locales.total, 'totalCost', this.state, this.renderTotalCost),
      simpleColumn(locales.invoice, this.renderInvoice)
    ];

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <ButtonBar style={{marginBottom: 8}}>
          <span>{locales.show_only_deleted}</span>
          <Switch style={{marginRight: 16, marginLeft: 4}} checked={showDeletedOnly}
                  onChange={this.onShowDeletedOnlyChange}/>

          <Button size="small" type="primary" icon="plus" onClick={this.onCreateSaleClick}>
            {locales.add}
          </Button>

        </ButtonBar>

        <Table
          dataSource={this.state.data}
          columns={columns}
          rowKey={tableUtils.indexRowKeyFn}
          bordered={true}
          size="small"
          pagination={tableUtils.defaultPaginationConfig(this.state)}
          onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
          scroll={{x: 960}}
          onRowClick={this.onRowClick}
        />

        {error && <ErrorAlert style={{marginTop: '16px'}} error={error}/>}
      </>
    );
  }

  private onRowClick = (record: InventorySale) => {
    this.props.history.push(viewSalePath(record.id!!));
  }

  private renderCreatedAt = (record: InventorySale): React.ReactNode => {
    return dateUtils.formatDateTimeOrNull(record.createdAt);
  };

  private renderLastChangedAt = (record: InventorySale): React.ReactNode => {
    return dateUtils.formatDateTimeOrNull(record.lastChangedAt);
  };

  private renderLastChangedBy = (record: InventorySale): React.ReactNode => {
    return record.lastChangedBy || '-';
  };

  private renderClient = (record: InventorySale): React.ReactNode => {
    if (!record.client) {
      return undefined;
    }

    return <Link to={paths.viewClientPath(record.client.id)}>{record.client.name}</Link>;
  };

  private renderEntries = (record: InventorySale): React.ReactNode => {
    const entriesCount = record.entries.length;
    return `${groupNumber(entriesCount)} ${locales.pcs}`;
  };

  private renderTotalCost = (record: InventorySale): React.ReactNode => {
    return renderFixedPriceColumn(record.totalCost);
  };

  private renderInvoice = (record: InventorySale): React.ReactNode => {
    return <InvoiceCell finalized={false} invoice={record.invoice}/>;
  };

  private onCreateSaleClick = () => {
    this.props.history.push(paths.createInventorySalePath);
  };

  private onShowDeletedOnlyChange = (checked: boolean) => {
    this.setState({showDeletedOnly: checked, page: 1}, () => this.reload());
  };

  private onGridChange = (newState: GridState<InventorySale>) => {
    this.setState({...newState}, () => this.reload());
  };

  private reload = () => {
    const request: GridRequest = {...tableUtils.toGridRequest(this.state), deleted: this.state.showDeletedOnly};
    const promise = furoApi.sales.getSales(request)
      .then(this.onReloadSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };


  private onReloadSuccess = (response: PageResponseEnvelope<InventorySale>) => {
    this.setState({
      ...tableUtils.toGridState(response),
      error: undefined,
    });
  };


  private onApiError = (error: ErrorResponse) => {
    this.setState({error});
  };
}

export default withRouter(SalesPage);



// WEBPACK FOOTER //
// ./src/sales/SalesPage.tsx